import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import applicationService from "../../services/applicationService";
import jobOfferService from "../../services/jobOfferService";
import interviewService from "../../services/interviewService";
import { getErrorMessage } from "../../utils/errorHandler";
import PageHeader from "../../components/ui/PageHeader";
import FilterBar from "../../components/ui/FilterBar";
import { Plus, Trash2, Calendar, Clock, User, FileText, CheckCircle2, X } from "lucide-react";
import "../../styles/recruitment.css";
import "../../styles/dashboard.css";

const Applications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [applications, setApplications] = useState([]);
  const [jobOffers, setJobOffers] = useState([]);
  const [interviews, setInterviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterJobId, setFilterJobId] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const statuses = [
    { value: "PENDING", label: "En attente" },
    { value: "PRESELECTED", label: "Présélectionné" },
    { value: "INTERVIEW", label: "Entretien" },
    { value: "ACCEPTED", label: "Accepté" },
    { value: "REJECTED", label: "Refusé" },
  ];



  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [appsResponse, offersResponse] = await Promise.all([
        applicationService.getAll(),
        jobOfferService.getAll(),
      ]);
      
      const appsList = Array.isArray(appsResponse) 
        ? appsResponse 
        : (appsResponse?.data || []);
      const offersList = Array.isArray(offersResponse) 
        ? offersResponse 
        : (offersResponse?.data || []);
      
      setApplications(appsList);
      setJobOffers(offersList);

      const interviewsData = {};
      for (const app of appsList) {
        try {
          const response = await interviewService.getByApplication(app.id);
          if (response && response.exists && response.interview) {
            interviewsData[app.id] = response.interview;
          } else {
            interviewsData[app.id] = null;
          }
        } catch (err) {
          interviewsData[app.id] = null;
        }
      }
      setInterviews(interviewsData);
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      await applicationService.updateStatus(applicationId, newStatus);
      toast.success("Statut de la candidature mis à jour.");
      loadData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("èŠtes-vous sè»r de vouloir supprimer cette candidature ?")) {
      try {
        await applicationService.delete(id);
        toast.success("Candidature supprimée.");
        loadData();
      } catch (err) {
        toast.error(getErrorMessage(err));
      }
    }
  };

  const filteredApplications = applications.filter(app => {
    const candidateName = `${app.candidate?.first_name || ""} ${app.candidate?.last_name || ""}`.toLowerCase();
    const candidateEmail = (app.candidate?.email || "").toLowerCase();
    const q = searchTerm.toLowerCase();

    const matchesSearch = candidateName.includes(q) || candidateEmail.includes(q);
    const matchesJob = filterJobId === "all" || app.job_offer_id?.toString() === filterJobId;
    const matchesStatus = filterStatus === "all" || app.status === filterStatus;

    return matchesSearch && matchesJob && matchesStatus;
  });

  return (
    <div style={{ padding: "24px" }}>
      <PageHeader
        title="Gestion des Candidatures"
        subtitle="Suivez les dossiers des candidats, examinez les scores de matching et planifiez les entretiens."
        actions={
          <button
            onClick={() => navigate("/rh/applications/new")}
            className="btn btn-primary"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Plus size={18} />
            <span>Nouvelle candidature</span>
          </button>
        }
      />

          {/* Filter Bar */}
          <FilterBar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Rechercher un candidat par nom, email..."
            filters={[
              {
                id: "job",
                label: "Offre d'emploi",
                value: filterJobId,
                onChange: setFilterJobId,
                options: [
                  { value: "all", label: "Toutes les offres" },
                  ...jobOffers.map(o => ({ value: o.id.toString(), label: o.title }))
                ]
              },
              {
                id: "status",
                label: "Statut",
                value: filterStatus,
                onChange: setFilterStatus,
                options: [
                  { value: "all", label: "Tous les statuts" },
                  ...statuses.map(s => ({ value: s.value, label: s.label }))
                ]
              }
            ]}
            onReset={() => {
              setSearchTerm("");
              setFilterJobId("all");
              setFilterStatus("all");
            }}
          />

          {/* Applications Table */}
          <div className="ui-card" style={{ overflow: "hidden" }}>
            {loading ? (
              <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
                Chargement des candidatures...
              </div>
            ) : filteredApplications.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
                Aucune candidature ne correspond à vos filtres.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ backgroundColor: "var(--bg-subtle)", borderBottom: "1px solid var(--border)", color: "var(--text-muted)", fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Candidat</th>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Offre Visée</th>
                      <th style={{ padding: "12px 16px", textAlign: "center" }}>Statut</th>
                      <th style={{ padding: "12px 16px", textAlign: "left" }}>Entretien</th>
                      <th style={{ padding: "12px 16px", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app) => {
                      const job = jobOffers.find(o => o.id === app.job_offer_id);
                      const interview = interviews[app.id];

                      return (
                        <tr key={app.id} style={{ borderBottom: "1px solid var(--border)", transition: "var(--transition-fast)" }}>
                          {/* Candidate Name & Email */}
                          <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <div style={{ width: "34px", height: "34px", borderRadius: "50%", backgroundColor: "var(--primary-light)", color: "var(--primary)", fontWeight: "700", display: "grid", placeItems: "center" }}>
                                {app.candidate?.first_name ? app.candidate.first_name.charAt(0).toUpperCase() : "C"}
                              </div>
                              <div>
                                <div style={{ fontWeight: "600", color: "var(--text-main)" }}>
                                  {app.candidate?.first_name && app.candidate?.last_name 
                                    ? `${app.candidate.first_name} ${app.candidate.last_name}`
                                    : app.candidate?.first_name || app.candidate?.last_name || "Nom non renseigné"}
                                </div>
                                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                                  {app.candidate?.email || "Email non renseigné"}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Job Title */}
                          <td style={{ padding: "14px 16px", verticalAlign: "middle", fontWeight: "500", color: "var(--text-main)" }}>
                            {job ? job.title : "-"}
                          </td>

                          {/* Status select */}
                          <td style={{ padding: "14px 16px", textAlign: "center", verticalAlign: "middle" }}>
                            <select
                              value={app.status || "PENDING"}
                              onChange={(e) => handleStatusChange(app.id, e.target.value)}
                              style={{
                                padding: "4px 8px",
                                borderRadius: "var(--radius-pill)",
                                fontSize: "0.78rem",
                                fontWeight: "700",
                                border: "1px solid var(--border)",
                                backgroundColor: "var(--bg-main)",
                                cursor: "pointer"
                              }}
                            >
                              {statuses.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                              ))}
                            </select>
                          </td>

                          {/* Interview Status */}
                          <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                            {interview ? (
                              <div style={{ fontSize: "0.82rem", color: "var(--primary)", fontWeight: "600", display: "flex", alignItems: "center", gap: "4px" }}>
                                <Calendar size={14} />
                                {new Date(interview.scheduled_date).toLocaleDateString('fr-FR')} {interview.scheduled_time}
                              </div>
                            ) : (
                              <button
                                onClick={() => navigate(`/rh/interview-schedule/${app.id}`)}
                                className="btn btn-outline btn-small"
                                style={{ fontSize: "0.75rem", padding: "3px 8px" }}
                              >
                                Planifier
                              </button>
                            )}
                          </td>

                          {/* Actions */}
                          <td style={{ padding: "14px 16px", textAlign: "right", verticalAlign: "middle" }}>
                            <button onClick={() => handleDelete(app.id)} className="btn btn-icon" title="Supprimer">
                              <Trash2 size={16} style={{ color: "var(--status-rejected-text)" }} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
    </div>
  );
};

export default Applications;

